import { DeviceDto, Transaction } from "@/api/openapi";
import { AlertDto } from "@/api/query.schemas";
import { useIsCustomer } from "./useIsCustomer";
import { useCallback } from "react";
import { getSiteTag } from "@/utils/utils";

export const usePageLinksForUser = () => {
    const isCustomer = useIsCustomer();

    const basePath = isCustomer ? "" : "/manage-customers";

    const getDevicesLink = useCallback(() => `${basePath}/devices`, [basePath]);
    const getSitesLink = useCallback(() => `${basePath}/sites`, [basePath]);

    const getDeviceLink = useCallback(
        (
            device: Pick<DeviceDto, "deviceIdentity" | "deviceGroupTags">,
            trailingURL?: string,
        ) => {
            const siteTag = getSiteTag(device);

            return `${basePath}${getDeviceRelativeUrl(device.deviceIdentity, siteTag, trailingURL)}`;
        },
        [basePath],
    );

    const getDeviceLinkForAlert = useCallback(
        (alert: AlertDto, trailingURL?: string) =>
            `${basePath}${getDeviceRelativeUrl(alert.deviceIdentity!, alert.siteDeviceGroupTag, trailingURL)}`,
        [basePath],
    );

    const getSiteLink = useCallback(
        (siteTag: string) => `${basePath}${getSiteRelativeUrl(siteTag)}`,
        [basePath],
    );

    const getTransactionLink = useCallback(
        (
            transaction: Pick<Transaction, "deviceIdentity" | "transactionId">,
        ) => {
            // "unassigned" will get tidied up once the user routes to the page
            return `${basePath}/sites/unassigned/devices/${transaction.deviceIdentity}/sessions/${transaction.transactionId}`;
        },
        [basePath],
    );

    const getRfidLink = useCallback((rfidTagId: number) => {
        return `/settings/rfid/${rfidTagId}`;
    }, []);

    const getVehicleLink = useCallback(
        (vehicleId: number) => `/manage-fleet/vehicles/${vehicleId}`,
        [],
    );

    const getScheduleLink = useCallback(
        (deviceGroupTag: string) => {
            // Maintenance is nested under "/network" for customers, unlike
            // sites/devices which sit at the root, so basePath ("") can't be
            // used for the customer case.
            const maintenanceBasePath = isCustomer ? "/network" : basePath;
            return `${maintenanceBasePath}/maintenance/schedules/${deviceGroupTag}`;
        },
        [isCustomer, basePath],
    );

    return {
        getDeviceLink,
        getSiteLink,
        getDevicesLink,
        getDeviceLinkForAlert,
        getTransactionLink,
        getSitesLink,
        getVehicleLink,
        getRfidLink,
        getScheduleLink,
    };
};

const getDeviceRelativeUrl = (
    deviceIdentity: string,
    siteDeviceGroupTag?: string,
    trailingURL?: string,
) => {
    const parentPath = siteDeviceGroupTag
        ? `${getSiteRelativeUrl(siteDeviceGroupTag)}/`
        : "/";

    return `${parentPath}devices/${deviceIdentity}${trailingURL ?? ""}`;
};

const getSiteRelativeUrl = (siteTag: string) => `/sites/${siteTag}`;
